import React, { useState } from 'react';
import { useFinanceStore } from '../api/useFinanceStore';
import { FinancialReport, PeriodType, CustomPeriod, calculatePeriodRange } from '../api/financeApi';
import { FileText, Download, BarChart3, PieChart, TrendingUp, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import { formatZAR } from '../../../utils/formatters';
import DocumentActionsBar from './DocumentActionsBar';
import { generateReportPdf } from '../../../services/pdfService';

const reportConfig: Record<string, { name: string; icon: React.ReactNode; color: string; description: string }> = {
    PnL: { name: 'Profit & Loss', icon: <BarChart3 size={20} />, color: 'blue', description: 'Revenue, expenses, and net profit analysis' },
    BalanceSheet: { name: 'Balance Sheet', icon: <PieChart size={20} />, color: 'indigo', description: 'Assets, liabilities, and equity position' },
    CashFlow: { name: 'Cash Flow', icon: <TrendingUp size={20} />, color: 'green', description: 'Cash inflows, outflows, and pipeline value' }};

const colorClasses: Record<string, { bg: string; text: string; border: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' }};

const REPORT_PERIODS: { value: PeriodType; label: string }[] = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'custom', label: 'Custom' },
    { value: 'all', label: 'All' }];

const FinancialReports: React.FC = () => {
    const { generateReport, data, customPeriod } = useFinanceStore();
    const [expandedReport, setExpandedReport] = useState<FinancialReport | null>(null);
    const [loadingReport, setLoadingReport] = useState<string | null>(null);
    const [reportPeriod, setReportPeriod] = useState<PeriodType>('monthly');
    const [reportCustom, setReportCustom] = useState<CustomPeriod | null>(null);
    const [reportCustomStart, setReportCustomStart] = useState('');
    const [reportCustomEnd, setReportCustomEnd] = useState('');
    const [reportPdf, setReportPdf] = useState<Blob>(new Blob());
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const resolvedPeriodLabel = calculatePeriodRange(reportPeriod, new Date(), reportCustom || undefined).label;

    const handleGenerate = async (type: FinancialReport['type']) => {
        setLoadingReport(type);
        try {
            const effectiveCustom = reportCustom ?? customPeriod;
            const report = await generateReport(type, reportPeriod, effectiveCustom);
            setExpandedReport(report);
        } finally {
            setLoadingReport(null);
        }
    };

    const handleGenerateReportPdf = async (type: FinancialReport['type']) => {
        setIsGeneratingPdf(true);
        try {
            const effectiveCustom = reportCustom ?? customPeriod;
            const report = await generateReport(type, reportPeriod, effectiveCustom);
            const pdf = await generateReportPdf(report);
            setReportPdf(pdf);
        } catch (error) {
            console.error('Error generating report PDF:', error);
            setReportPdf(new Blob());
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleExport = async (type: FinancialReport['type']) => {
        setIsGeneratingPdf(true);
        try {
            const effectiveCustom = reportCustom ?? customPeriod;
            const report = await generateReport(type, reportPeriod, effectiveCustom);
            await useFinanceStore.getState().generateReport(type, reportPeriod, effectiveCustom);
            await useFinanceStore.getState().exportReport(report);
        } catch (error) {
            console.error('Error exporting report:', error);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleReportPeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as PeriodType;
        setReportPeriod(value);
        if (value !== 'custom') {
            setReportCustom(null);
        } else if (reportCustomStart && reportCustomEnd) {
            setReportCustom({ start: reportCustomStart, end: reportCustomEnd });
        }
    };

    const handleApplyCustomPeriod = () => {
        if (reportCustomStart && reportCustomEnd) {
            setReportCustom({ start: reportCustomStart, end: reportCustomEnd });
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Financial Reports</h3>
                    <p className="text-xs text-slate-400 mt-1">Dynamic reports generated from your synced financial data</p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={reportPeriod}
                        onChange={handleReportPeriodChange}
                        className="px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest bg-white border border-slate-200 text-slate-700 focus:outline-none focus:border-blue-400"
                    >
                        {REPORT_PERIODS.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                    {reportPeriod === 'custom' && (
                        <div className="flex items-center gap-2">
                            <input type="date" value={reportCustomStart} onChange={e => setReportCustomStart(e.target.value)}
                                className="px-2 py-1 rounded-lg text-xs border border-slate-200 focus:outline-none focus:border-blue-400" />
                            <span className="text-slate-400 text-xs">to</span>
                            <input type="date" value={reportCustomEnd} onChange={e => setReportCustomEnd(e.target.value)}
                                className="px-2 py-1 rounded-lg text-xs border border-slate-200 focus:outline-none focus:border-blue-400" />
                            <button onClick={handleApplyCustomPeriod} disabled={!reportCustomStart || !reportCustomEnd}
                                className="px-3 py-1 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
                                Apply
                            </button>
                        </div>
                    )}
                    <div className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-blue-200">
                        {resolvedPeriodLabel}
                    </div>
                </div>
            </div>

            {/* Report Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(Object.entries(reportConfig) as [FinancialReport['type'], typeof reportConfig.PnL][]).map(([type, config]) => {
                    const colors = colorClasses[config.color];
                    return (
                        <div key={type} className={`p-6 bg-white rounded-3xl shadow-sm border border-slate-200 group hover:border-blue-300 transition-all ${expandedReport?.type === type ? 'ring-2 ring-blue-200' : ''}`}>
                            <div className={`p-4 ${colors.bg} ${colors.text} rounded-2xl w-fit mb-4 group-hover:scale-110 transition-transform`}>
                                {config.icon}
                            </div>
                            <h4 className="font-bold text-slate-800 mb-1">{config.name}</h4>
                            <p className="text-xs text-slate-400 font-medium mb-4">{config.description}</p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        handleGenerate(type);
                                        handleGenerateReportPdf(type);
                                    }}
                                    disabled={loadingReport === type || isGeneratingPdf}
                                    className={`flex-1 flex items-center justify-center gap-2 bg-slate-50 text-slate-600 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all active:scale-95 border border-slate-200 disabled:opacity-50 ${
                                        loadingReport === type ? 'opacity-70 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {loadingReport === type ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-slate-600" />
                                    ) : (
                                        <BarChart3 size={12} />
                                    )}
                                </button>
                                {loadingReport === type || isGeneratingPdf ? (
                                    <span className="text-xs text-slate-600">Generating...</span>
                                ) : (
                                    <>
                                        <BarChart3 size={12} />
                                        <span className="ml-2 text-xs text-slate-600">Generate</span>
                                    </>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleExport(type)}
                                    disabled={isGeneratingPdf}
                                    className={`p-2.5 rounded-xl border ${colors.border} ${colors.bg} ${colors.text} hover:opacity-80 transition-all ${
                                        isGeneratingPdf ? 'opacity-70 cursor-not-allowed' : ''
                                    }`}
                                    title="Export CSV"
                                >
                                    <Download size={14} />
                                </button>
                                {isGeneratingPdf ? (
                                    <span className="text-xs text-slate-600">Generating PDF...</span>
                                ) : (
                                    <>
                                        <DocumentActionsBar
                                            kind="report"
                                            reference={type}
                                            pdf={reportPdf}
                                            documentLabel={`${config.name} — ${resolvedPeriodLabel}`}
                                            compact
                                            onAfterAction={() => handleGenerateReportPdf(type)}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Expanded Report Detail */}
            {expandedReport && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-xl ${colorClasses[expandedReport.type === 'PnL' ? 'blue' : expandedReport.type === 'BalanceSheet' ? 'indigo' : 'green'].bg} ${colorClasses[expandedReport.type === 'PnL' ? 'blue' : expandedReport.type === 'BalanceSheet' ? 'indigo' : 'green'].text}`}>
                                {reportConfig[expandedReport.type].icon}
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800">{reportConfig[expandedReport.type].name}</h4>
                                <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Period: {expandedReport.period}</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setExpandedReport(null)}
                            className="text-slate-400 hover:text-slate-600 p-2"
                        >
                            <ChevronUp size={18} />
                        </button>
                    </div>

                    {/* Line Items (Itemization) */}
                    {expandedReport.lineItems && expandedReport.lineItems.length > 0 && (
                        <div className="p-6 border-b border-slate-100">
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Itemized Breakdown</div>
                            <div className="grid gap-2">
                                {expandedReport.lineItems.map((item, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                        <div className="text-sm font-medium text-slate-600">{item.label}</div>
                                        <div className="text-sm font-bold text-slate-800">{formatZAR(item.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Summary Table */}
                    <div className="p-6">
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Summary</div>
                        <div className="grid gap-3 mb-6">
                            {Object.entries(expandedReport.data).map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                                    <div className="text-sm font-medium text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                    <div className="text-sm font-bold text-slate-800">
                                        {typeof value === 'number' && key.toLowerCase().includes('margin')
                                            ? `${value.toFixed(1)}%`
                                            : formatZAR(value)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* AI Insights */}
                        {expandedReport.insights.length > 0 && (
                            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                                <div className="flex items-center gap-2 mb-3">
                                    <Lightbulb size={14} className="text-amber-600" />
                                    <div className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Insights</div>
                                </div>
                                <div className="space-y-2">
                                    {expandedReport.insights.map((insight, i) => (
                                        <div key={i} className="text-xs text-amber-800 flex items-start gap-2">
                                            <span className="text-amber-400 mt-0.5">•</span>
                                            {insight}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancialReports;